﻿import clsx from 'clsx';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  type Ref,
} from 'react';
import { useIntl } from 'react-intl';

import { CalendarHeader } from './calendar-header/CalendarHeader.js';
import * as styles from './Calendar.sty.js';
import type {
  CalendarStrictValueType,
  CalendarValueType,
} from './Calendar.types.js';
import { CALENDAR_ERROR_MESSAGES } from './calendarMessages.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import type { ValidateFieldOptions } from '../form/form.types.js';
import { useInternalFormRegistration } from '../form/hooks/useInternalFormRegistration.js';
import type { FormControlRef, TimeRangePickerRef } from '../shared-types.js';
import { type ValidationState } from '../validation/types.js';
import { useValidationStateEmitter } from '../validation/useValidationStateEmitter.js';
import { SetTimeToNowButton } from './components/SetTimeToNowButton.js';
import { CalendarContext } from './contexts/CalendarContext.js';
import { UseCalendarProps, useCalendar } from './hooks/useCalendar.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import {
  getI18nValidationState,
  useFormFieldValidation,
} from '../forms-core/hooks/useFormFieldValidation.js';
import { useContainerFocus } from '../hooks/useContainerFocus.js';
import { useCalendarNavigation } from './hooks/useCalendarNavigation.js';
import type { CalendarViewType } from './types/shared-types.js';
import { SelectDayView } from './views/SelectDayView.js';
import { SelectMonthView } from './views/SelectMonthView.js';
import { SelectYearView } from './views/SelectYearView.js';
import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { isFullTimeframe, isTimeValue } from '../../core/time/helpers.js';
import { useCalendarStore } from '../../core/time/timeframeStore/useTimeframeStore.js';
import { validate } from '../../core/time/validation.js';
import { DataTestId } from '../../core/types/data-props.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import type { MessageTarget } from '../validation/message-routing.js';
import { useCustomMessageEmitter } from '../validation/useCustomMessageEmitter.js';

const COMPONENT_NAME = 'Calendar';

//#region types
/**
 * @internal
 */
export type CalendarType = 'single' | 'range';

/** @internal */
export type CalendarProps<Type extends CalendarType> = FormControlProps<
  CalendarValueType<Type>,
  (date: CalendarStrictValueType<Type>) => void,
  HTMLDivElement
> &
  StylingProps &
  MaskingProps &
  DataTestId & {
    /** The earliest selectable date. It will always consider the start of the day as time. */
    min?: string;
    /** The latest selectable date. It will always consider the end of the day as time. */
    max?: string;
    /** Determines the calendar type. Single allows the selection of one date and range allows a range selection with a from and to value.
     * @defaultValue 'single'
     */
    type?: Type;
    /**
     * The precision of the time shown in the display value.
     * @defaultValue 'minutes'
     */
    precision?: 'minutes' | 'seconds' | 'milliseconds';

    /**
     * Internal: associates this control with a `_Form` rendered outside its
     * React subtree via the native `form` attribute. Unstable.
     * @internal
     */
    _form?: string;
    /**
     * Internal: routes this control's native/Strato validation messages to the
     * field outlet (default), the enclosing form's outlet, or both. Unstable.
     * @internal
     */
    _messageTarget?: MessageTarget | MessageTarget[];
  };
//#endregion

/** Render the correct view depending on the currentView state. */
function renderView(view: CalendarViewType) {
  switch (view) {
    case 'month':
      return <SelectMonthView />;
    case 'year':
      return <SelectYearView />;
    default:
      return <SelectDayView />;
  }
}

function CalendarComponent<Type extends CalendarType = 'single'>(
  props: Omit<CalendarProps<Type>, 'value' | 'defaultValue' | 'onChange'>,
  forwardedRef: Ref<
    | FormControlRef<HTMLDivElement>
    | (Omit<FormControlRef<HTMLDivElement>, 'inputRef'> &
        Pick<TimeRangePickerRef<HTMLDivElement>, 'inputRef'>)
  >,
) {
  const {
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    id,
    disabled,
    'aria-disabled': ariaDisabled,
    required,
    name,
    _form,
    _messageTarget,
    readOnly = false,
    // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
    // @ts-ignore Strict mode transition - needs to be investigated
    type = 'single',
    'aria-label': ariaLabelProp,
    'aria-labelledby': ariaLabelledByProp,
    'aria-describedby': ariaDescribedByProp,
    'aria-details': ariaDetailsProp,
    onValidityChange,
    onFocus,
    onBlur,
    ...remainingProps
  } = props;

  const calendarValue = useCalendarStore((state) => state.calendarValue);
  const min = useCalendarStore((state) => state.min);
  const max = useCalendarStore((state) => state.max);
  const error = useCalendarStore((state) => state.calendarError);
  const precision = useCalendarStore((state) => state.precision);

  // Imperative Handle Construction
  const intl = useIntl();
  const calendarRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLButtonElement>(null);
  const ref = useRef<HTMLInputElement>(null);
  const refTo = useRef<HTMLInputElement>(null);
  const formValidityChangeRef = useRef<
    ((state: ValidationState) => void) | undefined
  >(undefined);
  const _emitValidationState = useValidationStateEmitter((state) =>
    formValidityChangeRef.current?.(state),
  );
  const emitValidationState = useCustomMessageEmitter(
    _emitValidationState,
    () => getI18nValidationState(intl, ref.current, 'manual'),
  );

  const { uniqueId, state, dispatch } = useCalendar<Type>({
    type,
  } as UseCalendarProps<Type>);

  useCalendarNavigation({
    calendarRef,
    view: state.currentView,
    dispatch,
  });

  const { dispatch: formMessageDispatch, state: formFieldMessagesState } =
    useFormFieldMessagesContext();
  const customMessagesRef = useRef(
    formFieldMessagesState?.customMessages ?? [],
  );
  customMessagesRef.current = formFieldMessagesState?.customMessages ?? [];

  const refValidate = useCallback(() => {
    formMessageDispatch?.({
      type: FormFieldMessagesAction.SETISTOUCHED,
      payload: { isTouched: true },
    });

    validate(calendarValue, min, max, {
      formMessageDispatch,
      formerError: error,
      precision,
      showError: true,
      checkOrder: false,
      ref,
      errorMessages: CALENDAR_ERROR_MESSAGES,
    });
  }, [min, max, formMessageDispatch, error, precision, ref, calendarValue]);

  const validateField = useCallback(
    async ({
      reveal,
      trigger,
    }: ValidateFieldOptions): Promise<ValidationState> => {
      if (reveal) {
        formMessageDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        validate(calendarValue, min, max, {
          formMessageDispatch,
          formerError: error,
          precision,
          showError: true,
          checkOrder: false,
          ref,
          errorMessages: CALENDAR_ERROR_MESSAGES,
        });
      }
      if (isTimeValue(calendarValue)) {
        return emitValidationState(
          getI18nValidationState(intl, ref.current, trigger),
        );
      }
      const fromState = getI18nValidationState(intl, ref.current, trigger);
      if (fromState.errors.length > 0) {
        return emitValidationState(fromState);
      }
      return emitValidationState(
        getI18nValidationState(intl, refTo.current, trigger),
      );
    },
    [
      formMessageDispatch,
      calendarValue,
      min,
      max,
      error,
      precision,
      emitValidationState,
      intl,
      ref,
      refTo,
    ],
  );

  const resetValidation = useCallback(() => {
    formMessageDispatch?.({
      type: FormFieldMessagesAction.SETISTOUCHED,
      payload: { isTouched: false },
    });
    for (const msg of customMessagesRef.current) {
      formMessageDispatch?.({
        type: FormFieldMessagesAction.REMOVEMESSAGE,
        payload: { id: msg.id, messageType: 'custom' },
      });
    }
  }, [formMessageDispatch]);

  const formRegistration = useInternalFormRegistration({
    id,
    name,
    form: _form,
    messageTarget: _messageTarget,
    onValidityChange,
    getValue: () => calendarValue,
    focus: () => headerRef.current?.focus(),
    validateField,
    resetValidation,
  });
  formValidityChangeRef.current = formRegistration.onValidityChange;

  useImperativeHandle(
    forwardedRef,
    ():
      | FormControlRef<HTMLDivElement>
      | (Omit<FormControlRef<HTMLDivElement>, 'inputRef'> &
          Pick<TimeRangePickerRef<HTMLDivElement>, 'inputRef'>) => {
      if (type === 'single') {
        return {
          element: calendarRef.current,
          inputRef: ref.current,
          validate() {
            refValidate();
            emitValidationState(
              getI18nValidationState(intl, ref.current, 'manual'),
            );
          },
          // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
          validateAsync: async () => {
            refValidate();
            return emitValidationState(
              getI18nValidationState(intl, ref.current, 'manual'),
            );
          },
          focus() {
            headerRef.current?.focus();
          },
        };
      }

      return {
        element: calendarRef.current,
        inputRef: { from: ref?.current, to: refTo?.current },
        validate() {
          refValidate();
          const fromState = getI18nValidationState(intl, ref.current, 'manual');
          if (fromState.errors.length > 0) {
            emitValidationState(fromState);
            return;
          }
          emitValidationState(
            getI18nValidationState(intl, refTo.current, 'manual'),
          );
        },
        // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
        validateAsync: async () => {
          refValidate();
          const fromState = getI18nValidationState(intl, ref.current, 'manual');
          if (fromState.errors.length > 0) {
            return emitValidationState(fromState);
          }
          return emitValidationState(
            getI18nValidationState(intl, refTo.current, 'manual'),
          );
        },
        focus() {
          headerRef.current?.focus();
        },
      };
    },
    [refValidate, type, emitValidationState, intl],
  );

  const {
    ariaProps,
    onChange: validationOnChange,
    onInvalid,
  } = useFormFieldValidation(
    ref,
    {},
    {
      'aria-label': ariaLabelProp,
      'aria-labelledby': ariaLabelledByProp,
      'aria-describedby': ariaDescribedByProp,
      'aria-details': ariaDetailsProp,
    },
    undefined,
    _emitValidationState,
  );

  useEffect(() => {
    validationOnChange();
  }, [calendarValue, validationOnChange]);

  const labelRef = useRef<string>(`calendar-body-label-${uniqueId}`);

  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  const getNativeProps = (suffix = '') => ({
    readOnly: readOnly,
    required,
    disabled,
    'aria-disabled': ariaDisabled,
    name: name && `${name}${suffix}`,
    id,
    form: _form,
  });

  const calenderContextValue = useMemo(
    () => ({ state, dispatch, readOnly }),
    [state, dispatch, readOnly],
  );

  // CalendarRoot may be rendered without a FormField provider (e.g. inside DatePicker's
  // popover). useContainerFocus calls useFormFieldMessagesContext internally; the
  // dispatch is safely optional so SETISTOUCHED is a no-op when no context is present.
  const { containerClassName, ...containerProps } =
    useContainerFocus<HTMLDivElement>({
      onFocus,
      onBlur,
      disabled,
    });

  return (
    <CalendarContext.Provider value={calenderContextValue}>
      <div
        className={clsx(
          containerClassName,
          styles.calendar({ readOnly }),
          consumerClassName,
          {
            [styles.disabled]: disabledOrAriaDisabled,
          },
        )}
        role="group"
        data-testid={dataTestId}
        ref={calendarRef}
        style={consumerStyle}
        {...remainingProps}
        {...containerProps}
        data-dt-component={COMPONENT_NAME}
      >
        <CalendarHeader
          ref={headerRef}
          view={state.currentView}
          labelId={labelRef.current}
        />
        <div aria-labelledby={labelRef.current}>
          <table
            role="grid"
            className={styles.body({
              isReadOnly: !!readOnly,
            })}
          >
            {renderView(state.currentView)}
          </table>
        </div>
        {type === 'range' && <SetTimeToNowButton />}
      </div>
      <VisuallyHidden>
        {isTimeValue(calendarValue) ? (
          <input
            onChange={validationOnChange}
            onInvalid={onInvalid}
            {...ariaProps}
            {...getNativeProps()}
            ref={ref}
            value={calendarValue?.absoluteDate ?? ''}
            tabIndex={-1}
          />
        ) : (
          <>
            <input
              onChange={validationOnChange}
              onInvalid={onInvalid}
              {...ariaProps}
              {...getNativeProps('-from')}
              ref={ref}
              // Only set if a complete timeframe is available to rely on the browser validation for required.
              value={
                isFullTimeframe(calendarValue) && calendarValue?.from?.value
                  ? calendarValue.from.value
                  : ''
              }
              tabIndex={-1}
            />
            {/* This input is ONLY used for form submission and not for validation. */}
            <input
              {...getNativeProps('-to')}
              ref={refTo}
              value={calendarValue?.to?.value ?? ''}
              id={`${id}-to`}
              type="hidden"
            />
          </>
        )}
      </VisuallyHidden>
    </CalendarContext.Provider>
  );
}

CalendarComponent['displayName'] = COMPONENT_NAME;

/**
 * Calendar component
 * Renders the calendar header and body depending on the current view
 * @internal
 */
export const CalendarRoot = forwardRef(CalendarComponent);
